import React, { useCallback, useMemo, useState } from 'react';
import {
  BdsAvatar,
  BdsButton,
  BdsCheckbox,
  BdsGrid,
  BdsIcon,
  BdsModal,
  BdsSwitch,
  BdsTableCell,
  BdsTableRow,
  BdsTooltip,
  BdsTypo,
} from 'blip-ds/dist/blip-ds-react';
import { BdsCheckboxCustomEvent } from 'blip-ds/loader';
import { formatPhoneNumber } from '../../../utils/phoneNumber';
import { WhatsAppApplication } from '../typing/models/whatsapp-application.interface';

interface ListItemProps {
  bot: WhatsAppApplication;
  translate: Record<string, string>;
  onEnableCalling: (application: WhatsAppApplication, enabled: boolean) => Promise<boolean>;
  onEnableIconVisibility: (application: WhatsAppApplication, enabled: boolean) => Promise<boolean>;
  onEnableCallbackPermission: (application: WhatsAppApplication, enabled: boolean) => Promise<boolean>;
  onChangeSelectBot: (event: BdsCheckboxCustomEvent<any>) => void;
  onOpenModalStatus: (ownerIdentity: string) => void;
}

const ListItem: React.FC<ListItemProps> = ({
  bot,
  translate,
  onEnableCalling,
  onEnableIconVisibility,
  onEnableCallbackPermission,
  onChangeSelectBot,
  onOpenModalStatus,
}) => {
  const [isLoadingEnableCalling, setLoadingEnableCalling] = useState(false);
  const [isLoadingDisableCalling, setLoadingDisableCalling] = useState(false);
  const [isLoadingEnableCallback, setLoadingEnableCallback] = useState(false);
  const [isLoadingDisableCallback, setLoadingDisableCallback] = useState(false);

  const _onEnableCalling = useCallback(
    (enable: boolean) => {
      enable ? setLoadingEnableCalling(true) : setLoadingDisableCalling(true);
      return onEnableCalling(bot, enable).finally(() => enable ? setLoadingEnableCalling(false) : setLoadingDisableCalling(false));
    },
    [bot.identity],
  );

  const _onEnableIconVisibility = useCallback(
    (enabled: boolean) => {
      setLoadingEnableCalling(true);
      return onEnableIconVisibility(bot, enabled).finally(() => setLoadingEnableCalling(false));
    },
    [bot.identity],
  );

  const _onEnableCallbackPermission = useCallback(
    (enable: boolean) => {
      enable ? setLoadingEnableCallback(true) : setLoadingDisableCallback(true);
      return onEnableCallbackPermission(bot, enable).finally(() => enable ? setLoadingEnableCallback(false) : setLoadingDisableCallback(false));
    },
    [bot.identity],
  );

  return (
    <BdsTableRow className={bot.selected ? 'selected' : ''}>
      <BdsTableCell>
        <BdsCheckbox
          disabled={!bot.hasPermission}
          name={bot.id}
          checked={bot.selected}
          onBdsChange={onChangeSelectBot}
          label={''}
          refer={''}
        />
      </BdsTableCell>
      <BdsTableCell>
        <BdsGrid direction="row" alignItems="center" gap="2">
          {bot.imageUri ? (
            <BdsAvatar thumbnail={bot.imageUri} />
          ) : (
            <BdsIcon name="blip-chat" type="logo" size="xxx-large" className="round-avatar" />
          )}
          <BdsTypo bold="bold" variant="fs-16" margin={false} className={!bot.hasPermission ? 'label' : ''}>
            {bot.name}
          </BdsTypo>
        </BdsGrid>
      </BdsTableCell>
      <BdsTableCell>
        <BdsTypo variant="fs-14" bold="regular" margin={false}>
          {bot.shortName}
        </BdsTypo>
      </BdsTableCell>
      <BdsTableCell>
        <BdsTooltip tooltip-text={translate.hasNotPermission} disabled={bot.hasPermission}>
          {bot.hasPermission ? (
            <BdsGrid direction="row" alignItems="center" gap="2">
              <BdsButton
                key={`bds-button-disable-callback-${bot.id}`}
                variant="ghost"
                bdsLoading={isLoadingDisableCallback}
                disabled={isLoadingDisableCallback}
                onClick={() => _onEnableCallbackPermission(false)}
              >
                {translate.disable}
              </BdsButton>
              <BdsButton
                key={`bds-button-enable-callback-${bot.id}`}
                bdsLoading={isLoadingEnableCallback}
                disabled={isLoadingEnableCallback}
                onClick={() => _onEnableCallbackPermission(true)}
              >
                {translate.enable}
              </BdsButton>
            </BdsGrid>
          ) : (
            <BdsIcon name="lock" className="label" />
          )}
        </BdsTooltip>
      </BdsTableCell>
      <BdsTableCell>
        <BdsTooltip tooltip-text={translate.hasNotPermission} disabled={bot.hasPermission}>
          {bot.hasPermission ? (
            <BdsGrid direction="row" alignItems="center" gap="2">
              <BdsButton
                key={`bds-button-disable-calls-${bot.id}`}
                variant="ghost"
                bdsLoading={isLoadingDisableCalling}
                disabled={isLoadingDisableCalling}
                onClick={() => _onEnableCalling(false)}
              >
                {translate.disable}
              </BdsButton>
              <BdsButton
                key={`bds-button-enable-calls-${bot.id}`}
                bdsLoading={isLoadingEnableCalling}
                disabled={isLoadingEnableCalling}
                onClick={() => _onEnableCalling(true)}
              >
                {translate.enable}
              </BdsButton>
            </BdsGrid>
          ) : (
            <BdsIcon name="lock" className="label" />
          )}
        </BdsTooltip>
      </BdsTableCell>
      <BdsTableCell>
        <BdsButton
          key={`bds-button-check-status-${bot.id}`}
          variant="tertiary"
          icon='info'
          onClick={() => onOpenModalStatus(bot.shortName)}
        >
        </BdsButton>
      </BdsTableCell>
    </BdsTableRow>
  );
};

export default ListItem;
